import { useCallback, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { Label, IPersonaProps, ListPeoplePicker, IBasePickerSuggestionsProps } from "@fluentui/react";
import { FiHelpCircle } from "react-icons/fi";

import styles from "./GroupPicker.module.css"

import { getToken } from "../../authConfig";
import { handleError } from "../../toaster";
import { getUserGroups, PermissionsGroup, searchGroups } from "../../api";

interface Props {
  selectedGroups: PermissionsGroup[];
  onGroupsChange: (updatedGroups: PermissionsGroup[]) => void;
  isAdmin: boolean;
  useDarkMode: boolean;
};

export const GroupPicker = ({ selectedGroups, onGroupsChange, isAdmin, useDarkMode }: Props) => {
  const client = useMsal().instance;
  const [filterText, setFilterText] = useState<string>("");
  const tooltipHtml = "Select groups to be given access to the file(s)";
  const suggestionProps: IBasePickerSuggestionsProps = {
    suggestionsHeaderText: 'Suggested Groups',
    noResultsFoundText: filterText.length < 3 ? 'Type 3 or more characters' : 'No results found',
    loadingText: 'Loading',
  };

  const onPickerChange = (items?: IPersonaProps[]) => {
    if (items) {
      const newGroups = items.map(item => ({ id: item.key as string, displayName: item.text as string }));
      onGroupsChange(newGroups);
    }
  };

  const onRemoveGroup = (item: IPersonaProps): void => {
    const updatedGroups = selectedGroups.filter(group => group.id !== item.key);
    onGroupsChange(updatedGroups);
  };

  const onFilterChanged = useCallback(async (filterText: string): Promise<IPersonaProps[]> => {
    setFilterText(filterText);
    if (filterText.length >= 3) {
      try {
        const token = client ? await getToken(client) : undefined;
        // Admins can search all groups
        // Users can only search for groups they are a member of
        const result = isAdmin ? await searchGroups(token, filterText) : await getUserGroups(token);
        const groupSuggestions: IPersonaProps[] = result
          .filter(group => !selectedGroups.some(selected => selected.id === group.id))
          .map(group => ({
            key: group.id,
            text: group.displayName,
          }));
        return groupSuggestions.filter(persona => persona.text?.toLowerCase().includes(filterText.toLowerCase()));
      } catch (error) {
        handleError(error, "An error occurred fetching groups");
      }
    }
    return [];
  }, [selectedGroups]);

  return (
    <div className={styles.contentArea}>
      <div className={styles.pickerArea}>
        <Label>Groups&nbsp;
          <FiHelpCircle
            data-tooltip-id="GroupPicker-tooltip"
            data-tooltip-html={tooltipHtml}>
          </FiHelpCircle>
        </Label>
        <ListPeoplePicker
          onResolveSuggestions={onFilterChanged}
          getTextFromItem={(item: IPersonaProps) => item.text || ''}
          onChange={onPickerChange}
          pickerSuggestionsProps={suggestionProps}
          selectedItems={selectedGroups.map(group => ({
            key: group.id,
            text: group.displayName,
          }))}
          inputProps={{
            placeholder: 'Select groups to add',
          }}
          onRemoveSuggestion={onRemoveGroup}
          resolveDelay={300}
        />
      </div>
    </div>
  );
};
